// How to connect: what the app's developers need, read-only, every value with
// a copy button. Endpoints from POST /api/v1/idp/settings (internal/idp/idp.go
// EndpointsFor). OpenID Connect and MFA API apps open with "Connect your app"
// (the AuthNull URL or Discovery URL, the client ID and, for an app with a
// secret, its hint and Rotate secret); the other URLs are under
// "API endpoints (for developers)". An MFA API app uses the redirect flow
// (docs/MFA-API.md §2): after its own password check its server pushes a
// request (/oauth2/par), sends the browser to AuthNull's approve page
// (/oauth2/authorize) and swaps the code it gets back on its redirect URI at
// /oauth2/token, all at the tenant's address (the issuer without
// /api/v1/idp). Who may use it is decided by its app policies.
import React, { useEffect, useState } from "react";
import { Button, Collapse, Spin, Tabs, message } from "antd";
import { useNavigate } from "react-router-dom";
import { BookOpen, Download, Info, Plus, TriangleAlert } from "lucide-react";

import { CountChip } from "../../design-system/tableUi";
import { AN_COLORS } from "../../design-system/tokens";
import { logOutUser } from "../../common";
import { listAppPolicies } from "../../service/appPolicyService";
import { getIdpSettings, tenantBaseUrl } from "../../service/idpService";
import { CodeBlock, CopyButton, FieldLabel, InlineValue, Note, PanelHead, card } from "../enduser/ssh-proxy/sshProxyUi";
import { MFA_API_CLIENTS } from "./mfaApiClients";
import { HelpText, RotateSecretButton, SecretHint, ValueRow, hasRotatableSecret, reportIdpError, settingsBlock } from "./appsUi";

const MFA_API_GUIDE = "https://github.com/authnull0/authnull-service/blob/main/docs/MFA-API.md";

// The IdP signing certificate, from the SAML metadata, as a PEM file.
const downloadCertificate = async (metadataUrl) => {
  const res = await fetch(metadataUrl, { credentials: "omit" });
  if (!res.ok) throw new Error(`The metadata answered ${res.status}.`);
  const text = await res.text();
  const m = text.match(/<(?:\w+:)?X509Certificate>([\s\S]*?)<\/(?:\w+:)?X509Certificate>/);
  const body = (m ? m[1] : "").replace(/\\[nrt]/g, "").replace(/\s+/g, "");
  if (!body || !/^[A-Za-z0-9+/=]+$/.test(body)) throw new Error("No signing certificate in the metadata.");
  const lines = body.match(/.{1,64}/g) || [];
  const pem = `-----BEGIN CERTIFICATE-----\n${lines.join("\n")}\n-----END CERTIFICATE-----\n`;
  const url = URL.createObjectURL(new Blob([pem], { type: "application/x-pem-file" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "authnull-idp-signing.pem";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const rows = (list) => list.map((r, i) => <ValueRow key={r.label} first={i === 0} {...r} />);

// The client secret row: its hint, when it was made, and Rotate secret. The
// secret itself is never shown here.
function SecretRow({ app, issuer, onRotated, short }) {
  return (
    <div style={{ padding: "10px 0", borderTop: `1px solid ${AN_COLORS.line2}` }}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(8rem, 12rem) minmax(0, 1fr) auto", gap: 14, alignItems: "center" }}>
        <div style={{ fontSize: 12.5, fontWeight: 500, color: AN_COLORS.ink2 }}>Client secret</div>
        <SecretHint oidc={app.oidc} />
        <RotateSecretButton app={app} issuer={issuer} onRotated={onRotated} size="small" />
      </div>
      <HelpText>
        The secret is shown only once, when it's created or rotated. If it's lost, rotate it and update the app.
        {!short && " It can't be shown again: AuthNull keeps only a hash, like Azure."}
      </HelpText>
    </div>
  );
}

// One numbered setup step.
function Step({ n, children }) {
  return (
    <li style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <span
        style={{
          width: "1.375rem",
          height: "1.375rem",
          borderRadius: "50%",
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: AN_COLORS.bg,
          border: `1px solid ${AN_COLORS.line}`,
          color: AN_COLORS.ink2,
          fontSize: 11.5,
          fontWeight: 600,
        }}
      >
        {n}
      </span>
      <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: AN_COLORS.ink2, lineHeight: 1.55, paddingTop: 1 }}>{children}</div>
    </li>
  );
}

// The other URLs, collapsed.
function EndpointsSection({ list }) {
  return (
    <div style={{ ...card, padding: "4px 6px" }}>
      <Collapse
        ghost
        size="small"
        items={[
          {
            key: "endpoints",
            label: <span style={{ fontSize: 12.5, fontWeight: 500, color: AN_COLORS.ink2 }}>API endpoints (for developers)</span>,
            children: rows(list),
          },
        ]}
      />
    </div>
  );
}

const linkButton = { padding: 0, height: "auto", fontSize: 13 };

// "Add it to your app": the client from docs/MFA-API.md section 3 for each
// language (the client class, then its login + callback example), as static
// text, with Copy and the full guide.
function ClientCode() {
  const [lang, setLang] = useState(MFA_API_CLIENTS[0].key);
  return (
    <div style={{ ...card, padding: 18, minWidth: 0 }}>
      <PanelHead
        title="Add it to your app"
        desc="A small client for your app's server. After your password check, authUrl gives the address to send the browser to; in your callback, exchange swaps the code for the signed-in user and when their session ends."
      />
      <Tabs
        activeKey={lang}
        onChange={setLang}
        size="small"
        items={MFA_API_CLIENTS.map((c) => ({
          key: c.key,
          label: c.label,
          children: (
            <div style={{ display: "grid", gap: 10, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12.5, color: AN_COLORS.muted }}>{c.title}</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
                  <Button
                    type="link"
                    style={linkButton}
                    icon={<BookOpen size={13} style={{ verticalAlign: -2 }} />}
                    href={MFA_API_GUIDE}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Full guide
                  </Button>
                  <CopyButton text={c.code} />
                </span>
              </div>
              <div style={{ maxHeight: "28rem", overflow: "auto", borderRadius: 8, minWidth: 0 }}>
                <CodeBlock>{c.code}</CodeBlock>
              </div>
            </div>
          ),
        }))}
      />
    </div>
  );
}

export default function HowToConnect({ app, active = true, onSecretRotated, onOpenPolicies, onOpenSettings }) {
  const navigate = useNavigate();
  const [msg, msgCtx] = message.useMessage();
  const [endpoints, setEndpoints] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // The MFA API app's policies; null while unknown.
  const [policyCount, setPolicyCount] = useState(null);

  const isMfaApi = app?.protocol === "mfa_api";
  const appId = app?.id;
  // Read again each time the tab is shown: policies change on their tab.
  useEffect(() => {
    if (!isMfaApi || !appId || !active) return undefined;
    let alive = true;
    listAppPolicies({ appId })
      .then((list) => alive && setPolicyCount(list.length))
      .catch((f) => {
        if (f?.logout) logOutUser();
        if (alive) setPolicyCount(null);
      });
    return () => {
      alive = false;
    };
  }, [isMfaApi, appId, active]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getIdpSettings()
      .then((res) => alive && setEndpoints(res?.endpoints || {}))
      .catch((err) => {
        if (!alive) return;
        setFailed(true);
        reportIdpError(err, { msg, fallback: "Could not read the sign-in endpoints." });
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [msg]);

  if (loading) {
    return (
      <div style={{ ...card, padding: 40, textAlign: "center" }}>
        {msgCtx}
        <Spin />
      </div>
    );
  }
  const e = endpoints || {};
  const addPolicy = () => navigate(`/pam/policies/apps/new?app=${encodeURIComponent(appId)}`);
  const clientId = app?.oidc?.clientId || "";
  const redirectCount = (app?.oidc?.redirectUris || []).filter((u) => String(u).trim()).length;

  let body;
  if (app?.protocol === "saml") {
    const download = () => {
      setDownloading(true);
      downloadCertificate(e.samlMetadataUrl)
        .catch((err) => msg.error(err?.message || "Could not download the certificate."))
        .finally(() => setDownloading(false));
    };
    body = (
      <>
        <PanelHead title="SAML" desc="Give these to the app's SAML settings." />
        {rows([
          { label: "IdP metadata URL", hint: "Most SAML apps need only this", value: e.samlMetadataUrl },
          { label: "IdP entity ID", hint: "Issuer", value: e.samlEntityId },
          { label: "Single sign-on URL", hint: "HTTP-Redirect or HTTP-POST", value: e.samlSsoUrl },
        ])}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(8rem, 12rem) minmax(0, 1fr)",
            gap: 14,
            alignItems: "center",
            padding: "10px 0",
            borderTop: `1px solid ${AN_COLORS.line2}`,
          }}
        >
          <div style={{ fontSize: 12.5, fontWeight: 500, color: AN_COLORS.ink2 }}>Signing certificate</div>
          <div>
            <Button
              size="small"
              icon={<Download size={13} style={{ verticalAlign: -2 }} />}
              loading={downloading}
              disabled={!e.samlMetadataUrl}
              onClick={download}
            >
              Download certificate
            </Button>
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <Note tone="info" icon={<Info size={15} />}>
            In your app's SAML settings, paste the metadata URL, or enter the entity ID, SSO URL and certificate. AuthNull
            signs both the response and the assertion (RSA-SHA256). The sign-in must start from your app (SP-initiated).
          </Note>
        </div>
      </>
    );
  } else {
    const secret = hasRotatableSecret(app);
    // The tenant's address: where the app makes both its calls (token and MFA).
    const base = tenantBaseUrl(e.issuer);
    const connect = (
      <div style={{ ...card, padding: 18 }}>
        <PanelHead
          title="Connect your app"
          desc={
            isMfaApi
              ? "Your app's server uses these. Never put the client secret in a browser or desktop client."
              : "Give these to the app's developers."
          }
        />
        {rows([
          isMfaApi
            ? { label: "AuthNull URL", value: base, help: "Your app calls AuthNull, and sends its users to it, at this address. It's specific to your organisation." }
            : { label: "Discovery URL", hint: "Most apps need only this", value: e.discoveryUrl },
          { label: "Client ID", value: clientId },
        ])}
        {secret && <SecretRow app={app} issuer={e.issuer} onRotated={onSecretRotated} short={isMfaApi} />}
      </div>
    );

    if (isMfaApi) {
      const env = settingsBlock({ issuer: e.issuer, clientId, secret: "<the secret you copied when it was created>" });
      body = (
        <>
          {connect}
          <div style={{ ...card, padding: 18, display: "grid", gap: 14 }}>
            <div>
              <FieldLabel>App settings</FieldLabel>
              <CodeBlock multiline copyText={env}>
                {env}
              </CodeBlock>
            </div>
            <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 12 }}>
              <Step n={1}>Give these three values to the app's developers, or enter them in the app's settings.</Step>
              <Step n={2}>
                <span>
                  Register the app's callback as a redirect URI (Settings tab), e.g.{" "}
                  <InlineValue>https://erp.example.com/authnull/callback</InlineValue>.
                </span>
                {redirectCount === 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Note tone="warn" icon={<TriangleAlert size={15} />}>
                      This app has no redirect URI yet: AuthNull can't send its users back to it.
                      {onOpenSettings && (
                        <>
                          {" "}
                          <Button type="link" style={linkButton} onClick={onOpenSettings}>
                            Add one in Settings
                          </Button>
                        </>
                      )}
                    </Note>
                  </div>
                )}
              </Step>
              <Step n={3}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span>Create a policy for each user of this app.</span>
                  {policyCount != null && (
                    <Button type="link" style={linkButton} onClick={onOpenPolicies}>
                      <CountChip count={policyCount} label={policyCount === 1 ? "policy" : "policies"} />
                    </Button>
                  )}
                  <Button size="small" icon={<Plus size={13} style={{ verticalAlign: -2 }} />} onClick={addPolicy}>
                    Add policy
                  </Button>
                </div>
                {policyCount === 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Note tone="warn" icon={<TriangleAlert size={15} />}>
                      No one can use this app until a policy allows them.
                    </Note>
                  </div>
                )}
              </Step>
              <Step n={4}>
                After its own password check, the app sends the user to AuthNull for the second factor, and gets them back
                on its redirect URI.
              </Step>
            </ol>
          </div>
          <EndpointsSection
            list={[
              {
                label: "Pushed request",
                hint: "POST, from the server",
                help: "Client ID and secret; login_hint, redirect_uri, state, nonce. Answers a request_uri.",
                value: base && `${base}/api/v1/idp/oauth2/par`,
              },
              {
                label: "Browser",
                hint: "302 the user here",
                help: "?client_id=<client ID>&request_uri=<from the pushed request>. The request_uri works once, for 90 seconds.",
                value: base && `${base}/api/v1/idp/oauth2/authorize`,
              },
              {
                label: "Token",
                hint: "POST, from the server",
                help: "authorization_code grant: the code from the callback, client ID and secret. Answers the ID token.",
                value: base && `${base}/api/v1/idp/oauth2/token`,
              },
              {
                label: "Keys",
                hint: "To verify the ID token elsewhere",
                value: base && `${base}/api/v1/idp/oauth2/jwks`,
              },
            ]}
          />
          <ClientCode />
        </>
      );
    } else {
      body = (
        <>
          {connect}
          <EndpointsSection
            list={[
              { label: "Issuer", value: e.issuer },
              { label: "Authorization", value: e.authorizationEndpoint },
              { label: "Token", value: e.tokenEndpoint },
              { label: "Userinfo", value: e.userinfoEndpoint },
              { label: "JWKS", value: e.jwksUrl },
              { label: "Revocation", value: e.revocationEndpoint },
              { label: "Sign-out", value: e.endSessionEndpoint },
            ]}
          />
          <Note tone="info" icon={<Info size={15} />}>
            Your app's developers add an OpenID Connect login using the Discovery URL and Client ID. Use the authorization
            code flow with PKCE.
            {app?.oidc?.tokenEndpointAuthMethod === "none" ? (
              <> This app is a public client: it has no secret.</>
            ) : (
              <>
                {" "}
                The app authenticates at the token endpoint with its client secret (
                <InlineValue>{app?.oidc?.tokenEndpointAuthMethod || "client_secret_basic"}</InlineValue>).
              </>
            )}
          </Note>
        </>
      );
    }
  }

  if (failed && !endpoints) {
    return (
      <div style={{ ...card, padding: 18, maxWidth: "56rem" }}>
        {msgCtx}
        <Note tone="danger">The sign-in endpoints could not be read. Reload the page to try again.</Note>
      </div>
    );
  }
  if (app?.protocol === "saml") {
    return (
      <div style={{ ...card, padding: 18, maxWidth: "56rem" }}>
        {msgCtx}
        {body}
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gap: 16, maxWidth: "56rem" }}>
      {msgCtx}
      {body}
    </div>
  );
}
